import * as React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import { Button } from '#/shared/ui/button'
import { EditorEmpty } from '#/shared/ui/editor-empty'
import { LinkChainIcon, NoteEditIcon, SendIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'
import { Textarea } from '#/shared/ui/textarea'
import { Tooltip } from '#/shared/ui/tooltip'

const NOTE_MAX_LENGTH = 250

type ContractStatus = 'not_signed' | 'signed'

interface Contract {
  id: string
  recipient: string
  sentAt: string
  signedAt?: string
  status: ContractStatus
  previewUrl?: string
  cca?: boolean
  note?: string
  signLink?: string
}

interface ContractEditorProps {
  contracts?: Contract[]
  description?: string
  onSend?: () => void
  onSendNew?: () => void
  onPreview?: (contract: Contract) => void
  onToggleCca?: (contract: Contract, next: boolean) => Promise<void> | void
  onSaveNote?: (contract: Contract, note: string) => Promise<void> | void
  onCopySignLink?: (contract: Contract) => void
  isSending?: boolean
  isSendingNew?: boolean
  className?: string
  compact?: boolean
  onCancel?: () => void
}

function ContractEmpty({
  description,
  onSend,
  isSending,
}: {
  description: string
  onSend?: () => void
  isSending?: boolean
}) {
  const { t } = useTranslation()
  return (
    <EditorEmpty
      description={description}
      actionLabel={isSending ? t('table.saving') : t('editor.contract.send')}
      actionIcon={
        isSending ? (
          <span
            aria-hidden
            className="size-3 animate-spin rounded-full border-2 border-current border-t-transparent"
          />
        ) : (
          <SendIcon className="size-3" />
        )
      }
      actionDisabled={isSending}
      onAction={onSend}
    />
  )
}

function StatusPill({ status }: { status: ContractStatus }) {
  const { t } = useTranslation()
  const isSigned = status === 'signed'
  return (
    <span
      className={cn(
        'flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        isSigned
          ? 'bg-green-100 text-green-500'
          : 'bg-yellow-100 text-yellow-700',
      )}
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          isSigned ? 'bg-green-500' : 'bg-yellow-700',
        )}
      />
      {isSigned ? t('editor.contract.signed') : t('editor.contract.awaiting')}
    </span>
  )
}

function CcaToggle({
  checked,
  onChange,
  horizontal,
}: {
  checked: boolean
  onChange?: (next: boolean) => Promise<void> | void
  horizontal?: boolean
}) {
  const { t } = useTranslation()
  const interactive = Boolean(onChange)
  const [optimistic, setOptimistic] = useState<boolean | null>(null)
  const [pending, setPending] = useState(false)

  useEffect(() => {
    setOptimistic(null)
  }, [checked])

  const display = optimistic ?? checked

  const handleClick = async () => {
    if (!onChange || pending) return
    const next = !display
    setOptimistic(next)
    try {
      setPending(true)
      await onChange(next)
    } catch {
      setOptimistic(null)
    } finally {
      setPending(false)
    }
  }

  return (
    <div
      className={cn(
        horizontal
          ? 'flex items-center gap-1.5'
          : 'flex flex-col items-center justify-center gap-0.5',
      )}
    >
      <span
        className={cn(
          'leading-none text-gray-900',
          horizontal ? 'text-sm-minus font-medium' : 'text-sm-minus',
        )}
      >
        {t('editor.contract.cca')}
      </span>
      <button
        type="button"
        role="switch"
        aria-checked={display}
        aria-label={t('editor.contract.cca')}
        disabled={!interactive || pending}
        onClick={() => void handleClick()}
        className={cn(
          'relative inline-flex h-4.5 w-8 shrink-0 items-center rounded-full transition-colors',
          display ? 'bg-primary' : 'bg-gray-200',
          interactive && !pending ? 'cursor-pointer' : 'cursor-default',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'absolute size-3.5 rounded-full bg-white transition-all',
            display ? 'right-0.5' : 'left-0.5',
          )}
        />
      </button>
    </div>
  )
}

function AddNotePopover({
  contract,
  onSaveNote,
  hideIcon,
}: {
  contract: Contract
  onSaveNote: (c: Contract, note: string) => Promise<void> | void
  hideIcon?: boolean
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState(contract.note ?? '')
  const [isSaving, setIsSaving] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const trimmed = value.trim()
  const hasNote = Boolean(contract.note?.trim())

  useEffect(() => {
    if (open) {
      setValue(contract.note ?? '')
      textareaRef.current?.focus({ preventScroll: true })
    }
  }, [open, contract.note])

  const handleSubmit = async () => {
    if (!trimmed || isSaving) return
    try {
      setIsSaving(true)
      await onSaveNote(contract, trimmed)
      setOpen(false)
    } catch {
      return
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (isSaving && !next) return
        setOpen(next)
      }}
      side="bottom"
      align="right"
      portal
      trigger={() => (
        <Button
          type="button"
          variant="outline"
          size="xs"
          pressEffect
          className="gap-1.5"
        >
          {!hideIcon && <NoteEditIcon className="size-3.5" />}
          {hasNote
            ? t('editor.contract.editNote')
            : t('editor.contract.addNote')}
        </Button>
      )}
    >
      {(close) => (
        <div className="w-72 rounded-md border border-gray-200 bg-white p-2.5 shadow-popover">
          <div className="flex flex-col items-end gap-2.5">
            <Textarea
              ref={textareaRef}
              value={value}
              maxLength={NOTE_MAX_LENGTH}
              onChange={(e) =>
                setValue(e.target.value.slice(0, NOTE_MAX_LENGTH))
              }
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.preventDefault()
                  if (!isSaving) close()
                  return
                }
                if (
                  e.key === 'Enter' &&
                  (e.ctrlKey || e.metaKey) &&
                  trimmed &&
                  !isSaving
                ) {
                  e.preventDefault()
                  void handleSubmit()
                }
              }}
              disabled={isSaving}
            />
            <div className="w-full text-right text-xs text-gray-400 select-none">
              {value.length}/{NOTE_MAX_LENGTH}
            </div>
            <div className="flex items-center justify-end gap-1.5">
              <Button
                type="button"
                variant="outline-primary"
                size="xxs"
                onClick={close}
                disabled={isSaving}
              >
                {t('editor.contract.cancel')}
              </Button>
              <Button
                type="button"
                size="xxs"
                disabled={!trimmed || isSaving}
                onClick={() => void handleSubmit()}
              >
                {isSaving ? t('table.saving') : t('editor.contract.saveNote')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Popover>
  )
}

function CopySignLinkButton({
  contract,
  onCopySignLink,
}: {
  contract: Contract
  onCopySignLink?: (c: Contract) => void
}) {
  const { t } = useTranslation()
  if (!contract.signLink || !onCopySignLink) return null
  return (
    <Tooltip content={t('editor.contract.copySignLink')}>
      <Button
        type="button"
        variant="outline"
        size="xs"
        pressEffect
        aria-label={t('editor.contract.copySignLink')}
        className="px-1.5"
        onClick={() => onCopySignLink(contract)}
      >
        <LinkChainIcon className="size-3.5" />
      </Button>
    </Tooltip>
  )
}

function ContractRow({
  contract,
  onPreview,
  onToggleCca,
  onSaveNote,
  onCopySignLink,
}: {
  contract: Contract
  onPreview?: (c: Contract) => void
  onToggleCca?: (c: Contract, next: boolean) => void
  onSaveNote?: (c: Contract, note: string) => Promise<void> | void
  onCopySignLink?: (c: Contract) => void
}) {
  const { t } = useTranslation()
  const canPreview = Boolean(contract.previewUrl)
  const isSigned = contract.status === 'signed'
  return (
    <div className="flex w-full items-center justify-between gap-2.5 px-4 py-3">
      <div className="flex min-w-0 flex-1 flex-col gap-1 text-sm text-gray-500">
        <p>
          {t('editor.contract.sentTo')}{' '}
          <span className="font-medium text-gray-900">
            {contract.recipient}
          </span>
        </p>
        <p className="text-xs">{contract.sentAt}</p>
      </div>
      <div className="flex shrink-0 items-center gap-2.5">
        {isSigned && contract.signedAt && (
          <span className="text-2xs-plus font-medium text-green-500">
            {contract.signedAt}
          </span>
        )}
        <StatusPill status={contract.status} />
        {!isSigned && (
          <>
            <CcaToggle
              checked={Boolean(contract.cca)}
              onChange={
                onToggleCca ? (next) => onToggleCca(contract, next) : undefined
              }
            />
            {onSaveNote && (
              <AddNotePopover contract={contract} onSaveNote={onSaveNote} />
            )}
          </>
        )}
        <CopySignLinkButton
          contract={contract}
          onCopySignLink={onCopySignLink}
        />
        <Button
          variant="outline"
          size="xs"
          pressEffect
          disabled={!canPreview}
          onClick={() => onPreview?.(contract)}
        >
          {t('editor.contract.view')}
        </Button>
      </div>
    </div>
  )
}

function ContractList({
  contracts,
  onPreview,
  onSendNew,
  onToggleCca,
  onSaveNote,
  onCopySignLink,
  isSendingNew,
}: {
  contracts: Contract[]
  onPreview?: (c: Contract) => void
  onSendNew?: () => void
  onToggleCca?: (c: Contract, next: boolean) => void
  onSaveNote?: (c: Contract, note: string) => Promise<void> | void
  onCopySignLink?: (c: Contract) => void
  isSendingNew?: boolean
}) {
  const { t } = useTranslation()
  return (
    <div className="flex max-h-110 w-full flex-col">
      <div className="flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
        {contracts.map((c, i) => (
          <React.Fragment key={c.id}>
            {i > 0 && (
              <div className="px-4">
                <span className="block h-px w-full bg-gray-200" />
              </div>
            )}
            <ContractRow
              contract={c}
              onPreview={onPreview}
              onToggleCca={onToggleCca}
              onSaveNote={onSaveNote}
              onCopySignLink={onCopySignLink}
            />
          </React.Fragment>
        ))}
      </div>
      <div className="flex h-14 w-full shrink-0 items-center justify-end gap-2.5 border-t border-gray-200 px-2.5">
        <Button
          size="xs"
          pressEffect
          disabled={isSendingNew}
          className="gap-2.5 px-3.75"
          onClick={onSendNew}
        >
          {isSendingNew ? (
            <span
              aria-hidden
              className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
            />
          ) : (
            <SendIcon className="size-3.5" />
          )}
          {isSendingNew ? t('table.saving') : t('editor.contract.sendNew')}
        </Button>
      </div>
    </div>
  )
}

function CompactStatusPill({ status }: { status: ContractStatus }) {
  const { t } = useTranslation()
  const isSigned = status === 'signed'
  return (
    <span
      className={cn(
        'flex shrink-0 items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium',
        isSigned ? 'bg-green-50 text-green-700' : 'bg-amber-100 text-amber-700',
      )}
    >
      <span
        className={cn(
          'size-1.5 rounded-full',
          isSigned ? 'bg-green-700' : 'bg-amber-700',
        )}
      />
      {isSigned ? t('editor.contract.signed') : t('editor.contract.awaiting')}
    </span>
  )
}

function CompactContractCard({
  contract,
  onPreview,
  onToggleCca,
  onSaveNote,
  onCopySignLink,
}: {
  contract: Contract
  onPreview?: (c: Contract) => void
  onToggleCca?: (c: Contract, next: boolean) => void
  onSaveNote?: (c: Contract, note: string) => Promise<void> | void
  onCopySignLink?: (c: Contract) => void
}) {
  const { t } = useTranslation()
  const isSigned = contract.status === 'signed'
  const canPreview = Boolean(contract.previewUrl)
  return (
    <div className="flex flex-col gap-2.5 rounded-lg border border-gray-200 bg-white p-3.5">
      <p className="text-sm text-gray-500">
        {t('editor.contract.sentTo')}{' '}
        <span className="font-medium text-gray-900">{contract.recipient}</span>{' '}
        <span className="text-xs text-gray-400">{contract.sentAt}</span>
      </p>
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          <CompactStatusPill status={contract.status} />
          {isSigned && contract.signedAt && (
            <span className="truncate text-xs text-gray-400">
              {contract.signedAt}
            </span>
          )}
        </div>
        <CopySignLinkButton
          contract={contract}
          onCopySignLink={onCopySignLink}
        />
        <Button
          variant="outline"
          size="xs"
          pressEffect
          disabled={!canPreview}
          onClick={() => onPreview?.(contract)}
        >
          {t('editor.contract.view')}
        </Button>
      </div>
      {!isSigned && (
        <>
          <span className="block h-px w-full bg-gray-200" />
          <div className="flex items-center gap-2.5">
            <CcaToggle
              horizontal
              checked={Boolean(contract.cca)}
              onChange={
                onToggleCca ? (next) => onToggleCca(contract, next) : undefined
              }
            />
            {onSaveNote && (
              <AddNotePopover
                contract={contract}
                onSaveNote={onSaveNote}
                hideIcon
              />
            )}
          </div>
        </>
      )}
    </div>
  )
}

function CompactContractList({
  contracts,
  onPreview,
  onSendNew,
  onToggleCca,
  onSaveNote,
  onCopySignLink,
  isSendingNew,
  onCancel,
}: {
  contracts: Contract[]
  onPreview?: (c: Contract) => void
  onSendNew?: () => void
  onToggleCca?: (c: Contract, next: boolean) => void
  onSaveNote?: (c: Contract, note: string) => Promise<void> | void
  onCopySignLink?: (c: Contract) => void
  isSendingNew?: boolean
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col">
      <div className="flex flex-col gap-4 p-4">
        {contracts.map((c) => (
          <CompactContractCard
            key={c.id}
            contract={c}
            onPreview={onPreview}
            onToggleCca={onToggleCca}
            onSaveNote={onSaveNote}
            onCopySignLink={onCopySignLink}
          />
        ))}
      </div>
      <div className="sticky bottom-0 z-10 flex h-14 items-center justify-end gap-2.5 border-t border-gray-200 bg-gray-100 px-4">
        {onCancel && (
          <Button variant="outline" size="xs" pressEffect onClick={onCancel}>
            {t('editor.contract.cancel')}
          </Button>
        )}
        <Button
          size="xs"
          pressEffect
          disabled={isSendingNew}
          onClick={onSendNew}
        >
          {isSendingNew ? (
            <span
              aria-hidden
              className="size-3.5 animate-spin rounded-full border-2 border-current border-t-transparent"
            />
          ) : (
            <SendIcon className="size-3.5" />
          )}
          {isSendingNew ? t('table.saving') : t('editor.contract.sendNew')}
        </Button>
      </div>
    </div>
  )
}

function ContractEditor({
  contracts = [],
  description,
  onSend,
  onSendNew,
  onPreview,
  onToggleCca,
  onSaveNote,
  onCopySignLink,
  isSending,
  isSendingNew,
  className,
  compact,
  onCancel,
}: ContractEditorProps) {
  const { t } = useTranslation()
  const resolvedDescription = description ?? t('editor.contract.description')
  if (compact && contracts.length > 0) {
    return (
      <CompactContractList
        contracts={contracts}
        onPreview={onPreview}
        onSendNew={onSendNew}
        onToggleCca={onToggleCca}
        onSaveNote={onSaveNote}
        onCopySignLink={onCopySignLink}
        isSendingNew={isSendingNew}
        onCancel={onCancel}
      />
    )
  }
  return (
    <div className={cn('flex w-full flex-col', className)}>
      {contracts.length === 0 ? (
        <ContractEmpty
          description={resolvedDescription}
          onSend={onSend}
          isSending={isSending}
        />
      ) : (
        <ContractList
          contracts={contracts}
          onPreview={onPreview}
          onSendNew={onSendNew}
          onToggleCca={onToggleCca}
          onSaveNote={onSaveNote}
          onCopySignLink={onCopySignLink}
          isSendingNew={isSendingNew}
        />
      )}
    </div>
  )
}

export { ContractEditor }
export type { ContractEditorProps, Contract, ContractStatus }
